import { App } from "@capacitor/app";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { supabase } from "./supabase";

interface AppUpdatePlugin {
  pickAndInstall(): Promise<{ opened: boolean }>;
  downloadAndInstall(options: { url: string }): Promise<{ opened: boolean }>;
}

export interface ReleaseManifest {
  version: string;
  apkPath: string;
}

export interface AppUpdateAvailability {
  installedVersion: string;
  installedBuild: string;
  latestVersion: string;
  available: boolean;
}

const AppUpdate = registerPlugin<AppUpdatePlugin>("AppUpdate");
const RELEASE_BUCKET = "android-releases";
const LATEST_APK_PATH = "latest/Just-Kasz-CRM-arm64.apk";
const LATEST_MANIFEST_PATH = "latest/manifest.json";
let availabilityRequest: Promise<AppUpdateAvailability | null> | null = null;

export function canInstallFromFile(): boolean {
  return Capacitor.isNativePlatform();
}

function versionParts(value: string): number[] | null {
  const core = value.trim().replace(/^v/, "").split("-", 1)[0];
  if (!core) return null;
  const parts = core.split(".");
  if (parts.length === 0 || parts.some((part) => !/^\d+$/.test(part))) return null;
  return parts.map(Number);
}

export function isNewerVersion(latest: string, installed: string): boolean {
  const latestParts = versionParts(latest);
  const installedParts = versionParts(installed);
  if (!latestParts || !installedParts) return false;

  const length = Math.max(latestParts.length, installedParts.length);
  for (let index = 0; index < length; index += 1) {
    const latestPart = latestParts[index] ?? 0;
    const installedPart = installedParts[index] ?? 0;
    if (latestPart > installedPart) return true;
    if (latestPart < installedPart) return false;
  }
  return false;
}

export function parseReleaseManifest(value: unknown): ReleaseManifest {
  if (
    !value ||
    typeof value !== "object" ||
    !("version" in value) ||
    typeof value.version !== "string" ||
    !versionParts(value.version)
  ) {
    throw new Error("The latest Android release information is invalid");
  }
  const apkPath =
    "apk_path" in value && typeof value.apk_path === "string" ? value.apk_path : LATEST_APK_PATH;
  if (
    apkPath !== LATEST_APK_PATH &&
    !/^releases\/[0-9]+\.[0-9]+\.[0-9]+\/Just-Kasz-CRM-arm64\.apk$/.test(apkPath)
  ) {
    throw new Error("The latest Android release information is invalid");
  }
  return { version: value.version, apkPath };
}

async function latestReleaseManifest(): Promise<ReleaseManifest> {
  if (!supabase) throw new Error("Supabase has not been configured");

  const { data, error } = await supabase.storage
    .from(RELEASE_BUCKET)
    .download(LATEST_MANIFEST_PATH);
  if (error || !data) throw new Error("The latest Android release could not be checked");

  try {
    return parseReleaseManifest(JSON.parse(await data.text()) as unknown);
  } catch {
    throw new Error("The latest Android release information is invalid");
  }
}

export async function checkForAppUpdate(
  options: { force?: boolean } = {},
): Promise<AppUpdateAvailability | null> {
  if (!Capacitor.isNativePlatform()) return null;
  if (!options.force && availabilityRequest) return availabilityRequest;

  const request = Promise.all([App.getInfo(), latestReleaseManifest()]).then(([info, release]) => ({
    installedVersion: info.version,
    installedBuild: info.build,
    latestVersion: release.version,
    available: isNewerVersion(release.version, info.version),
  }));
  availabilityRequest = request;

  try {
    return await request;
  } catch (error) {
    if (availabilityRequest === request) availabilityRequest = null;
    throw error;
  }
}

export async function installFromFile(): Promise<void> {
  await AppUpdate.pickAndInstall();
}

export async function downloadLatestUpdate(): Promise<void> {
  if (!supabase) throw new Error("Supabase has not been configured");

  const release = await latestReleaseManifest();
  const { data, error } = await supabase.storage
    .from(RELEASE_BUCKET)
    .createSignedUrl(release.apkPath, 300, {
      download: "Just-Kasz-CRM-latest-arm64.apk",
    });
  if (error || !data?.signedUrl) {
    throw new Error("The latest Android update is not available");
  }

  if (Capacitor.isNativePlatform()) {
    await AppUpdate.downloadAndInstall({ url: data.signedUrl });
    return;
  }

  const link = document.createElement("a");
  link.href = data.signedUrl;
  link.rel = "noreferrer";
  link.click();
}
